import React, { useState, useEffect, useCallback } from 'react';
import { MocChange, MocDashboardKPIs } from './types';
import { mocService } from './mocService';
import { MocMetricsCards } from './MocMetricsCards';
import { MocTable } from './MocTable';
import { MocDetailModal } from './MocDetailModal';
import { MocCreateModal } from './MocCreateModal';

interface Props {
  organizationId?: string;
}

export const ManagementOfChangePage: React.FC<Props> = ({ organizationId = 'ORG-XYZ' }) => {
  const [kpis, setKpis] = useState<MocDashboardKPIs | null>(null);
  const [changes, setChanges] = useState<MocChange[]>([]);
  const [selectedChangeId, setSelectedChangeId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpisData, changesData] = await Promise.all([
        mocService.getDashboardKPIs(organizationId),
        mocService.getChanges(organizationId)
      ]);
      setKpis(kpisData);
      setChanges(changesData);
    } catch (err) {
      console.error(err);
      setError('Failed to load MOC data: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="page-header-banner">
        <div className="badge">RISK & CHANGE CONTROL</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Management of Change (MOC)</h1>
            <p>Pre-screening, multi-domain impact analysis, risk assessment, independent SoD approval chains, and post-implementation reviews (PIR).</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + New Change Request
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* KPI Cards */}
      <MocMetricsCards kpis={kpis} onRefresh={loadData} />

      {/* Table */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white">Change Requests & MOC Register</h2>
        <MocTable changes={changes} onSelectChange={setSelectedChangeId} />
      </div>

      {/* Detail Modal */}
      {selectedChangeId && (
        <MocDetailModal
          changeId={selectedChangeId}
          organizationId={organizationId}
          onClose={() => setSelectedChangeId(null)}
          onRefresh={loadData}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <MocCreateModal
          organizationId={organizationId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};
